/**
 * YouTubeEmbed.tsx — Facade pattern + single-play coordination + single-iframe PIP
 *
 * Default render: a static thumbnail image + custom play button overlay.
 * On click (or keyboard Enter/Space): replaces the facade with the real
 * YouTube iframe and autoplays the video.
 *
 * Performance impact on the Ukrainian post (28 embeds):
 *   Before: 28 iframes request YouTube resources on scroll-into-view even
 *           with loading="lazy" — the browser still pre-connects and prefetches
 *           iframe resources, hammering CPU and network.
 *   After:  28 <img> thumbnails load lazily. Only the clicked embed ever
 *           requests YouTube. No YouTube JS, no youtube-nocookie.com connections
 *           until user intent is signalled.
 *
 * Thumbnail URL strategy:
 *   1. Try maxresdefault.jpg (1280×720, clean).
 *   2. On 404 or load error, fall back to hqdefault.jpg (480×360, always present).
 *   Both are served from i.ytimg.com — no YouTube JS or cookies.
 *
 * Editor mode detection:
 *   TinaCMS adds data-tina-field-overlay to the figure wrapper. When the
 *   TinaCMS admin is active, window.__tina_cms is defined. If the editor
 *   is detected at click time, the facade activates normally — the editor
 *   still gets playback. For true visual-editing of the embed block itself,
 *   the data-tina-field-overlay attribute on the <figure> provides the
 *   click-on-canvas affordance regardless of facade state.
 *
 * Accessibility:
 *   - Facade wrapper is a <button> with full keyboard support (Enter/Space).
 *   - aria-label: "Play YouTube video — <caption>" or fallback.
 *   - focus-visible ring using var(--gold) per brand kit.
 *   - Thumbnail img has descriptive alt text.
 *   - figcaption renders identically to the pre-facade implementation.
 *
 * Single-play coordination (UKRAINIAN-POST-VIDEO-COORDINATION-01 2026-05-06):
 *   When wrapped in <VideoCoordinatorProvider>, each embed participates in
 *   single-play coordination via VideoCoordinatorContext:
 *     - active embed renders iframe; all others render facade
 *     - clicking a new embed deactivates the previous one (its iframe unmounts)
 *     - close button (×) sets activeVideoId=null → all revert to facade
 *   If no provider is found (context returns null), falls back to self-contained
 *   local state — same pre-coordination behavior. This preserves use on other pages.
 *
 * Single-iframe PIP (PIP-RESTART-FIX-01 2026-05-06):
 *   Matches the About page VideoBeat pattern. A single iframe DOM node is used
 *   for both inline and floating states. The INNER container (.yt-embed__pip)
 *   toggles between:
 *     - inline:   position:absolute; inset:0  (fills the .yt-embed__ratio placeholder)
 *     - floating: position:fixed; bottom-right (inline style + class .pf-pip--floating)
 *   The iframe never unmounts during a scroll transition — only its container's
 *   CSS changes. This prevents the YouTube autoplay restart that occurred when
 *   FloatingMiniPlayer created a second iframe instance with ?autoplay=1.
 *
 *   IntersectionObserver is attached to the OUTER .yt-embed__ratio wrapper (the
 *   placeholder box that always stays in document flow). Threshold 0.1. Fires
 *   setFloating(true/false) when the wrapper leaves/enters the viewport.
 *
 *   Reduce-motion: IntersectionObserver is NOT attached when
 *   prefers-reduced-motion: reduce is active — iframe stays inline always.
 *
 * Brand kit tokens used for play button:
 *   --bg-scrim-play (#0E0A18 at 85%): button background (matches existing kit token)
 *   --gold (#D4B86E): play triangle fill + button border
 *   --radius-md (8px): button border-radius
 *   CSS lives in globals.css under .yt-embed scope (existing) +
 *   .yt-embed__facade additions (new, added in BLOG-WRAPPER-FIX-02).
 *
 * Security: videoId validated against /^[A-Za-z0-9_-]{11}$/ before interpolation.
 */

'use client';

import { useState, useCallback, useRef, useContext, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { VideoCoordinatorContext } from './VideoCoordinator';

const VIDEO_ID_REGEX = /^[A-Za-z0-9_-]{11}$/;

interface YouTubeEmbedProps {
  videoId: string;
  caption?: string | null;
  /**
   * Optional path to a local thumbnail image (e.g. /images/blog/.../thumbnail.webp).
   * When set, the facade uses this image instead of the YouTube auto-generated thumbnail.
   * The hqdefault fallback is disabled — local files are known-good.
   * Leave undefined to use default YouTube thumbnail behavior (maxresdefault → hqdefault).
   */
  customThumbnail?: string | null;
  /**
   * Optional id for the outermost <figure> wrapper.
   * Used as the smooth-scroll target for <watchVideoLink targetId="...">.
   * Added 2026-05-09 (video-blog migrations cycle 5).
   * Auto-generates `video-${videoId}` if not provided.
   */
  wrapperId?: string | null;
}

export default function YouTubeEmbed({ videoId, caption, customThumbnail, wrapperId }: YouTubeEmbedProps) {
  // Security: reject any videoId that doesn't match the expected 11-char format.
  if (!videoId || !VIDEO_ID_REGEX.test(videoId)) {
    return null;
  }

  return <YouTubeEmbedInner videoId={videoId} caption={caption} customThumbnail={customThumbnail} wrapperId={wrapperId} />;
}

function YouTubeEmbedInner({ videoId, caption, customThumbnail, wrapperId }: { videoId: string; caption?: string | null; customThumbnail?: string | null; wrapperId?: string | null }) {
  // Try to read the coordinator context. May be null if this embed is used outside
  // a <VideoCoordinatorProvider> (e.g. on other pages — falls back to local state).
  const coordinator = useContext(VideoCoordinatorContext);

  // Local active state — used when no coordinator context is present.
  const [localIsActive, setLocalIsActive] = useState(false);

  // Derive whether this embed is active (should show iframe):
  // - with coordinator: active iff coordinator.activeVideoId === videoId
  //   (no isFloating check needed — the iframe stays mounted during floating now)
  // - without coordinator: local state
  const isActive = coordinator
    ? coordinator.activeVideoId === videoId
    : localIsActive;

  // Floating state — true when the outer wrapper has scrolled out of viewport.
  // Only applies when active. Drives the .pf-pip--floating class + inline style.
  const [floating, setFloating] = useState(false);

  // If a custom local thumbnail is provided, use it directly.
  // Otherwise start with maxresdefault and fall back to hqdefault on error.
  const [thumbSrc, setThumbSrc] = useState(
    customThumbnail ?? `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
  );

  // outerRef: the .yt-embed__ratio placeholder box — always in document flow.
  // IntersectionObserver watches this to determine floating state.
  const outerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Privacy-enhanced embed with autoplay=1 so clicking once starts video.
  // playsinline=1 is required for iOS WebKit (Safari + all iOS browsers):
  // without it, iOS forces the video into full-screen mode or shows its own
  // play overlay, causing the two-click symptom Lars reported on mobile.
  // No UX cost — playsinline just means "play inline, not full-screen".
  const iframeSrc = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&playsinline=1`;

  // Detect reduce-motion preference once. When true, IntersectionObserver is
  // never attached — iframe stays inline always, no floating behavior.
  // Checked lazily at effect time (SSR-safe: useEffect only runs client-side).
  const prefersReducedMotionRef = useRef(false);

  // Attach IntersectionObserver when this embed becomes active.
  // Detach and reset floating when it becomes inactive.
  useEffect(() => {
    // Determine reduce-motion preference client-side
    prefersReducedMotionRef.current =
      typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    if (!isActive) {
      // Video deactivated — tear down observer, reset floating
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      setFloating(false);
      return;
    }

    // Active — attach observer (unless reduce-motion is on)
    if (prefersReducedMotionRef.current) return;
    if (!outerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setFloating(!entry.isIntersecting);
        });
      },
      { threshold: 0.1 },
    );
    observerRef.current.observe(outerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [isActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  const activate = useCallback(() => {
    // flushSync forces the React state update to happen synchronously inside the
    // user-gesture event handler. This keeps the iframe mount within the browser's
    // gesture window — required for iOS Safari autoplay (and eliminates the
    // double-click symptom where YouTube's native play button appeared paused).
    flushSync(() => {
      if (coordinator) {
        coordinator.setActiveVideo(videoId);
      } else {
        setLocalIsActive(true);
      }
    });
  }, [videoId, coordinator]);

  const closePip = useCallback(() => {
    if (coordinator) {
      coordinator.setActiveVideo(null);
    } else {
      setLocalIsActive(false);
    }
    setFloating(false);
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, [coordinator]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  }, [activate]);

  // Alt text: descriptive + caption-aware, per accessibility requirements.
  const altText = caption
    ? `YouTube video — ${caption}`
    : 'Click to play YouTube video';

  // aria-label for the play button: same pattern, announces the action.
  const ariaLabel = caption
    ? `Play YouTube video — ${caption}`
    : 'Play YouTube video';

  // Resolved wrapper id — explicit wrapperId prop takes precedence; fallback to video-${videoId}.
  const resolvedWrapperId = wrapperId ?? `video-${videoId}`;

  return (
    <figure className="yt-embed" id={resolvedWrapperId} data-tina-field-overlay>
      {/*
        OUTER wrapper — always in document flow, maintains the 16:9 placeholder box.
        IntersectionObserver watches this element to determine floating state.
        When the inner container is floating (position:fixed), the outer box
        acts as a visual placeholder (dark background, gold border) so the article
        layout does not collapse.
      */}
      <div
        className="yt-embed__ratio"
        ref={outerRef}
        data-videoid={videoId}
      >
        {isActive ? (
          /*
           * ACTIVE state: INNER container holds the iframe.
           *
           * Inline (not floating):
           *   .yt-embed__pip — position:absolute; inset:0 — fills the outer box.
           *
           * Floating (scrolled out of view):
           *   .yt-embed__pip.pf-pip--floating — position:fixed; bottom-right.
           *   The outer box stays as a dark placeholder in document flow.
           *   The iframe DOM node is the SAME element — only the container CSS changes.
           *   This prevents YouTube from restarting playback (no unmount/remount).
           *
           *   Positional/visual props carried via inline style so the floating wrapper
           *   matches the About-page VideoBeat pattern exactly (pf-pip--floating only,
           *   no yt-embed__pip--floating). This unifies the close-button rendering context
           *   across all pages. (PIP-CLOSE-UNIFY-01, 2026-05-23)
           *
           *   z-index: 9000 — below .blog-drawer (z-index 9500) per Lars's preference
           *   ("sidebar overlaps the video"). About uses 9999; blog intentionally lower.
           *
           * CSS for both states lives in globals.css under .yt-embed__pip.
           */
          <div
            className={`yt-embed__pip${floating ? ' pf-pip--floating' : ''}`}
            style={floating ? {
              position: 'fixed',
              inset: 'auto 24px 24px auto',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--video-frame-shadow-pip)',
              zIndex: 9000,
              overflow: 'hidden',
              transition: 'box-shadow 350ms cubic-bezier(0.4, 0, 0.2, 1)',
            } : undefined}
          >
            <iframe
              src={iframeSrc}
              title={caption ? `YouTube video player — ${caption}` : 'YouTube video player'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="yt-embed__iframe"
            />
            {/* Close button — only visible when floating */}
            {floating && (
              <button
                type="button"
                className="yt-floating-player__close"
                aria-label="Close floating video"
                onClick={closePip}
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
              </button>
            )}
          </div>
        ) : (
          /* ── FACADE: thumbnail + play button + crawlable noscript iframe ── */
          <>
            <button
              type="button"
              className="yt-embed__facade"
              aria-label={ariaLabel}
              onClick={activate}
              onKeyDown={handleKeyDown}
            >
              {/* Thumbnail — lazy-loaded, explicit dimensions for CLS prevention */}
              {/* onError only fires for YouTube auto-thumbnails; omitted when customThumbnail is set (local files are known-good). */}
              <img
                src={thumbSrc}
                alt={altText}
                className="yt-embed__thumb"
                loading="lazy"
                decoding="async"
                width="1280"
                height="720"
                onError={customThumbnail ? undefined : () => {
                  // maxresdefault not always available — fall back to hqdefault (480x360)
                  if (!thumbSrc.includes('hqdefault')) {
                    setThumbSrc(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`);
                  }
                }}
              />
              {/* Play button overlay */}
              <span className="yt-embed__play" aria-hidden="true">
                <svg
                  className="yt-embed__play-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="32"
                  height="32"
                  aria-hidden="true"
                  focusable="false"
                >
                  {/* Filled triangle pointing right — standard play icon */}
                  <path d="M8 5.14v14l11-7-11-7z" />
                </svg>
              </span>
            </button>
            {/* Crawlable player for Googlebot: real iframe present in SSR DOM at zero JS cost.
                The facade button above handles the visible UX; this noscript block makes the
                embed URL discoverable by crawlers that never execute JS (SOP-SEO-02). */}
            <noscript>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title={altText}
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                width="1280"
                height="720"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </noscript>
          </>
        )}
      </div>
      {caption && (
        <figcaption className="yt-embed__caption">{caption}</figcaption>
      )}
    </figure>
  );
}
